import type { MembershipDocument, WorkspaceMembershipRole } from '@myplayer/schema';
import { IconBrandShield, IconCheck, IconEdit, IconPlus } from '@myplayer/ui';
import type { FormEvent, ReactElement, ReactNode } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import { ASSIGNABLE_MEMBER_ROLES, formatWorkspaceName } from './auth-session.js';
import {
  ActionOverflowMenu,
  Badge,
  Button,
  Input,
  ModalDialog,
  Select,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildMemberContextMenuItems } from './context-menu-items.js';
import { FieldHint } from './help/field-hint.js';
import { formatPersonaRoleLabel } from './strings.js';
import { resolveAdminEmailPlaceholder } from './use-workspace-identifiers.js';

export {
  ASSIGNABLE_MEMBER_ROLES,
  isSelfMember,
  mapSnapshotToMemberships,
  resolveDisplayedMemberRole,
} from './auth-session.js';

export interface MemberWorkspaceOption {
  readonly workspaceId: string;
  readonly name: string;
}

const ROLE_VARIANT = (r: WorkspaceMembershipRole) =>
  r === 'ORG_ADMIN' ? 'role_owner' : r === 'WORKSPACE_MANAGER' ? 'role_admin' : 'role_member';
const STATUS_VARIANT = { ACTIVE: 'success', INVITED: 'accent', REVOKED: 'outline' } as const;
const STATUS_LABEL = { ACTIVE: 'Active', INVITED: 'Invited', REVOKED: 'Deactivated' } as const;
const LBL_CLS = 'mb-1.5 block text-[12px] font-medium text-fg-secondary';
const INVITE_HINT =
  'Each role grants specific capabilities. Organisation Admin applies across every workspace; Workspace Manager, Campaign Editor, Compliance Officer, and Viewer are scoped to the selected workspace.';
const EDIT_HINT =
  "Changing a member's role updates their permissions immediately. Assigning Organisation Admin grants access across all workspaces.";

export function MemberRow(props: {
  readonly member: MembershipDocument;
  readonly isSelf: boolean;
  readonly currentRole: WorkspaceMembershipRole;
  readonly canManageMembers: boolean;
  readonly submitting: boolean;
  readonly availableWorkspaces?: readonly MemberWorkspaceOption[];
  readonly onStartEditRole: (m: MembershipDocument, r: WorkspaceMembershipRole) => void;
  readonly onDeactivate: (m: MembershipDocument) => void;
}): ReactElement {
  const { member, isSelf, currentRole, canManageMembers, submitting } = props;
  const { bindContextMenu, contextMenuElement } = useContextMenu();
  const wsNameMap = new Map((props.availableWorkspaces ?? []).map((w) => [w.workspaceId, w.name]));
  const wsEntries = Object.entries(member.workspaceRoles).filter(([, r]) => r !== 'ORG_ADMIN');
  return (
    <tr
      data-testid={`member-row-${member.email}`}
      onContextMenu={bindContextMenu(() => buildMemberContextMenuItems(props))}
      className="h-12 transition-colors hover:bg-surface-muted"
    >
      <td className="px-3.5 py-2.5 align-middle text-[13px] font-medium text-fg">
        {member.email}
        {isSelf && <span className="ml-1 text-[11px] font-normal text-fg-secondary">(you)</span>}
        {contextMenuElement}
      </td>
      <td className="px-3.5 py-2.5 align-middle">
        <Badge variant={STATUS_VARIANT[member.status]}>{STATUS_LABEL[member.status]}</Badge>
      </td>
      <td className="px-3.5 py-2.5 align-middle">
        <div
          className="flex flex-wrap items-center gap-1.5"
          data-testid={`member-roles-${member.email}`}
        >
          {member.orgRole === 'ORG_ADMIN' ? (
            <Badge variant="role_owner" data-testid={`member-org-role-${member.email}`}>
              {formatPersonaRoleLabel('ORG_ADMIN')} (All workspaces)
            </Badge>
          ) : wsEntries.length > 0 ? (
            wsEntries.map(([wsId, r]) => (
              <Badge
                key={wsId}
                variant={ROLE_VARIANT(r)}
                data-testid={`member-ws-role-${member.email}-${wsId}`}
              >
                {wsNameMap.get(wsId) ?? formatWorkspaceName(undefined, wsId)}:{' '}
                {formatPersonaRoleLabel(r)}
              </Badge>
            ))
          ) : (
            <Badge variant={ROLE_VARIANT(currentRole)}>{formatPersonaRoleLabel(currentRole)}</Badge>
          )}
        </div>
      </td>
      {canManageMembers && (
        <td className="px-3.5 py-2.5 align-middle text-right">
          {!isSelf && member.status !== 'REVOKED' && (
            <div className="flex items-center justify-end gap-1.5">
              <Button
                type="button"
                variant="secondary"
                size="xs"
                disabled={submitting}
                aria-label={`Edit role for ${member.email}`}
                onClick={() => props.onStartEditRole(member, currentRole)}
              >
                <IconEdit />
                <span>Edit</span>
              </Button>
              <ActionOverflowMenu
                label={`More actions for ${member.email}`}
                size="xs"
                disabled={submitting}
                data-testid={`member-overflow-${member.email}`}
              >
                <Button
                  type="button"
                  variant="destructive"
                  size="xs"
                  disabled={submitting}
                  onClick={() => props.onDeactivate(member)}
                >
                  Deactivate
                </Button>
              </ActionOverflowMenu>
            </div>
          )}
        </td>
      )}
    </tr>
  );
}

function RolePermissionSummaryCard(p: {
  readonly role: WorkspaceMembershipRole;
  readonly testId: string;
}): ReactElement | null {
  const inRouter = useInRouterContext();
  const meta = ASSIGNABLE_MEMBER_ROLES.find((r) => r.value === p.role);
  if (!meta) return null;
  const linkClass = 'text-[11px] font-semibold text-brand hover:underline';
  const linkText = 'View full Roles & Permissions matrix in Help Centre →';
  return (
    <div
      data-testid={p.testId}
      className="mt-2.5 rounded-control border border-subtle bg-surface-muted p-3 text-[12px] leading-relaxed text-fg"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold text-fg">
          {meta.label} <span className="font-mono text-[11px] text-fg-muted">({meta.value})</span>
        </span>
        <Badge variant={meta.value === 'ORG_ADMIN' ? 'role_owner' : 'outline'}>{meta.scope}</Badge>
      </div>
      <p className="mt-1 text-fg-secondary">{meta.summary}</p>
      <div className="mt-2 grid grid-cols-1 gap-2 border-t border-subtle pt-2 sm:grid-cols-2">
        {(['Can do:', 'Cannot do:'] as const).map((heading, idx) => (
          <div key={heading}>
            <span className="block text-[11px] font-semibold text-fg">{heading}</span>
            <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-[11px] text-fg-secondary">
              {(idx === 0 ? meta.canDo : meta.cannotDo).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-2 border-t border-subtle pt-1.5 text-right">
        {inRouter ? (
          <Link to="/help/roles-and-permissions" className={linkClass}>
            {linkText}
          </Link>
        ) : (
          <a href="/help/roles-and-permissions" className={linkClass}>
            {linkText}
          </a>
        )}
      </div>
    </div>
  );
}

function renderRoleAndWorkspaceFields(
  mode: 'invite' | 'edit',
  role: WorkspaceMembershipRole,
  onRoleChange: (val: WorkspaceMembershipRole) => void,
  wsId: string | undefined,
  workspaces: readonly MemberWorkspaceOption[] | undefined,
  onWsChange?: (val: string) => void,
  email?: string,
  disabled?: boolean,
): ReactNode {
  const isInvite = mode === 'invite';
  const roleSelectId = isInvite ? 'invite-member-role' : 'edit-member-role-select';
  const roleLabel = isInvite ? 'Role' : `Change role for ${email ?? ''}`;
  const wsSelectId = isInvite ? 'invite-member-workspace' : 'edit-member-workspace-select';
  const wsList = workspaces ?? [];
  const wsEl =
    role !== 'ORG_ADMIN' && wsList.length > 0 && wsId !== undefined && onWsChange ? (
      <div key="ws">
        <label htmlFor={wsSelectId} className={LBL_CLS}>
          Target Workspace
        </label>
        <Select
          id={wsSelectId}
          aria-label="Target Workspace"
          data-testid={isInvite ? 'invite-member-workspace-select' : 'edit-member-workspace-select'}
          value={wsId}
          disabled={disabled}
          onChange={(e) => onWsChange(e.target.value)}
        >
          {wsList.map((ws) => (
            <option key={ws.workspaceId} value={ws.workspaceId}>
              {ws.name} ({ws.workspaceId})
            </option>
          ))}
        </Select>
      </div>
    ) : null;
  const roleEl = (
    <div key="role">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <label htmlFor={roleSelectId} className="block text-[12px] font-medium text-fg-secondary">
          {roleLabel}
        </label>
        <FieldHint
          label="Team Member Roles"
          hint={isInvite ? INVITE_HINT : EDIT_HINT}
          helpSlug="roles-and-permissions"
          testId={isInvite ? 'invite-role-hint' : 'edit-role-hint'}
        />
      </div>
      <Select
        id={roleSelectId}
        aria-label={roleLabel}
        value={role}
        disabled={disabled}
        onChange={(e) => onRoleChange(e.target.value as WorkspaceMembershipRole)}
      >
        {ASSIGNABLE_MEMBER_ROLES.map((r) => (
          <option key={r.value} value={r.value}>
            {r.label}
          </option>
        ))}
      </Select>
      <RolePermissionSummaryCard
        role={role}
        testId={isInvite ? 'invite-role-permission-summary' : 'edit-role-permission-summary'}
      />
    </div>
  );
  return isInvite ? [roleEl, wsEl] : [wsEl, roleEl];
}

function renderMemberRoleModal(
  mode: 'invite' | 'edit',
  open: boolean,
  subtitle: string,
  submitting: boolean,
  errorMessage: string | null,
  onClose: () => void,
  children: ReactNode,
  onSubmitClick?: () => void,
): ReactElement {
  const isInvite = mode === 'invite';
  const formId = isInvite ? 'invite-member-form' : undefined;
  return (
    <ModalDialog
      open={open}
      title={isInvite ? 'Invite Team Member' : 'Edit Member Role'}
      subtitle={subtitle}
      icon={isInvite ? <IconBrandShield /> : <IconEdit />}
      presentation="center"
      maxWidthClassName="max-w-xl"
      submitting={submitting}
      onClose={onClose}
      data-testid={isInvite ? 'invite-member-modal' : 'edit-member-role-modal'}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type={formId ? 'submit' : 'button'}
            form={formId}
            variant="primary"
            size="sm"
            disabled={submitting}
            onClick={onSubmitClick}
          >
            {isInvite ? <IconPlus /> : <IconCheck />}
            <span>{isInvite ? 'Invite Member' : 'Save Role'}</span>
          </Button>
        </>
      }
    >
      {children}
      {errorMessage !== null && (
        <p
          role="alert"
          className="mt-4 rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
        >
          {errorMessage}
        </p>
      )}
    </ModalDialog>
  );
}

export function InviteMemberModal(p: {
  readonly open: boolean;
  readonly workspaceId: string;
  readonly availableWorkspaces?: readonly MemberWorkspaceOption[];
  readonly onWorkspaceChange?: (val: string) => void;
  readonly inviteEmail: string;
  readonly setInviteEmail: (val: string) => void;
  readonly inviteRole: WorkspaceMembershipRole;
  readonly setInviteRole: (val: WorkspaceMembershipRole) => void;
  readonly submitting: boolean;
  readonly errorMessage: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (e: FormEvent) => void;
}): ReactElement {
  const wsName =
    (p.availableWorkspaces ?? []).find((ws) => ws.workspaceId === p.workspaceId)?.name ??
    formatWorkspaceName(undefined, p.workspaceId);
  const subtitle =
    p.inviteRole === 'ORG_ADMIN'
      ? 'Grant organisation-wide admin access across all workspaces'
      : `Grant access to workspace ${wsName}`;
  return renderMemberRoleModal(
    'invite',
    p.open,
    subtitle,
    p.submitting,
    p.errorMessage,
    p.onClose,
    <form id="invite-member-form" onSubmit={p.onSubmit} className="space-y-4">
      <div>
        <label htmlFor="invite-member-email" className={LBL_CLS}>
          Member Email
        </label>
        <Input
          id="invite-member-email"
          type="email"
          value={p.inviteEmail}
          onChange={(e) => p.setInviteEmail(e.target.value)}
          placeholder={resolveAdminEmailPlaceholder('colleague')}
          required
        />
      </div>
      {renderRoleAndWorkspaceFields(
        'invite',
        p.inviteRole,
        p.setInviteRole,
        p.workspaceId,
        p.availableWorkspaces,
        p.onWorkspaceChange,
      )}
    </form>,
  );
}

export function EditMemberRoleModal(p: {
  readonly target: MembershipDocument | null;
  readonly targetWorkspaceId?: string;
  readonly availableWorkspaces?: readonly MemberWorkspaceOption[];
  readonly onTargetWorkspaceChange?: (val: string) => void;
  readonly roleDraft: WorkspaceMembershipRole;
  readonly setRoleDraft: (val: WorkspaceMembershipRole) => void;
  readonly submitting: boolean;
  readonly errorMessage: string | null;
  readonly onClose: () => void;
  readonly onSave: (m: MembershipDocument, r: WorkspaceMembershipRole, wsId?: string) => void;
}): ReactElement {
  const { target, targetWorkspaceId, roleDraft, submitting } = p;
  return renderMemberRoleModal(
    'edit',
    target !== null,
    target ? `Update organisation or workspace role for ${target.email}` : '',
    submitting,
    p.errorMessage,
    p.onClose,
    target && (
      <div className="space-y-4">
        {renderRoleAndWorkspaceFields(
          'edit',
          roleDraft,
          p.setRoleDraft,
          targetWorkspaceId,
          p.availableWorkspaces,
          p.onTargetWorkspaceChange,
          target.email,
          submitting,
        )}
      </div>
    ),
    () => target && p.onSave(target, roleDraft, targetWorkspaceId),
  );
}
